import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

/** Material UI Packages. */
import { Box, Dialog } from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';
import InputField from 'shared/ui/InputForm';
import { callAPI } from 'shared/api/callApi';
import { MESSAGES, showSnackbar } from '../../../lib/configUtils';

const initialFormData = {
    id: randomId(),
    duty_name: '',
    formula: '',
    description: '',
};

export default function FormulaForm({
    dialogOpen,
    setDialogOpen,
    userEmail,
    getAccessTokenSilently,
    itemFormulasRows,
    setItemFormulasRows,
    setLoadingState,
}) {
    const dispatch = useDispatch();

    const [formData, setFormData] = useState(initialFormData);

    /** Check if form has any input values */
    const hasFormData = useCallback(() => {
        return formData.duty_name.trim() !== '' || formData.formula.trim() !== '' || formData.description.trim() !== '';
    }, [formData]);

    /** Handle dialog close with confirmation if form has data */
    const handleClose = useCallback(() => {
        if (hasFormData()) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
            if (!confirmed) {
                return;
            }
        }
        setFormData(initialFormData);
        setDialogOpen(false);
    }, [hasFormData, setDialogOpen]);

    const handleSave = () => {
        if (formData.duty_name.trim() === '' || formData.formula.trim() === '') {
            showSnackbar(dispatch, MESSAGES.EMPTY_FORMULA, 'warning');
            return;
        }

        callAPI({
            method: 'post',
            module: 'duty_calculator',
            component: 'duty_formula',
            getAccessTokenSilently,
            email: userEmail,
            obj: formData,
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: true }));
            },
            onFinal: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: false }));
            },
            onSuccess: (resp) => {
                setItemFormulasRows([...itemFormulasRows, resp.data]);
                setFormData(initialFormData);
                showSnackbar(dispatch, MESSAGES.ADD_SUCCESS, 'success');
                setDialogOpen(false);
            },
            onError: (error) => {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: error.message,
                        severity: 'error',
                    }),
                );
            },
        });
    };

    return (
        <Dialog
            maxWidth="sm"
            fullWidth={true}
            open={dialogOpen}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                    width: '600px',
                },
            }}
            PaperComponent={PaperComponent}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '250px' }}>
                <DialogHeader cursor title="Add Formula" onClose={handleClose} onSave={handleSave} />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2,
                        height: '100%',
                        justifyContent: 'center',
                    }}
                >
                    <InputField
                        label="Duty Name"
                        value={formData.duty_name}
                        onChange={(e) => setFormData({ ...formData, duty_name: e.target.value })}
                        inputStyle={{ width: '100%' }}
                    />
                    <InputField
                        label="Formula"
                        value={formData.formula}
                        onChange={(e) => setFormData({ ...formData, formula: e.target.value })}
                        inputStyle={{ width: '100%' }}
                    />
                    <InputField
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        inputStyle={{ width: '100%' }}
                    />
                </Box>
            </Box>
        </Dialog>
    );
}
